import type {AccessGrantAudience} from './access_grants.js';
import type {DestinationConnection} from '@gl/types/destinations.js';
import type {Nullable} from './general.js';
import type {
  PlatformType,
  MLBPrivacySettings,
  ZetaFieldMappings,
} from './journeys.js';
import type {MappedMatchField} from '@gl/types/journeys.js';
import type {Option} from '@gl/lit/components/base/multiselect_async/index.js';
import type {User} from './user.js';

export type AudienceCreator = 'user' | 'flywheeler' | 'suggestion' | 'api';
export enum AudienceType {
  DEFAULT = 'default',
  TEMPLATE = 'template',
}

export enum AudienceCategory {
  STANDARD = 'standard',
  SCRATCH = 'scratch',
  BASICS = 'basics',
  TEMPLATE = 'template',
  RECIPE = 'recipe',
  MARVE = 'marve',
  COPIED = 'copied',
}

export interface Policies {
  edit: boolean;
  new: boolean;
  view_raw_query: boolean;
  refresh_size: boolean;
  destroy: boolean;
  archive: boolean;
  unarchive: boolean;
  csv_export: boolean;
}

export interface Audience<
  UserT = unknown,
  TagsT = unknown,
  DestinationsT = unknown,
> {
  created_at: string;
  updated_at: string;
  id: number;
  origin_audience_id?: number;
  name: string;
  audience_type: AudienceType.DEFAULT;
  category: AudienceCategory.STANDARD;
  creator: AudienceCreator;
  creator_metadata?: User;
  dataset_id: number;
  dataset_group_id: number;
  dataset_group_name: string;
  description: string;
  query: string;
  parameterized_query?: string;
  status: string;
  calculated_status: string;
  reviewer_metadata?: User;
  reviewer_id?: number;
  user_id: number;
  team_id: number;
  size: number;
  last_refreshed_size_at: string;
  revenue_touched?: number;
  control?: number;
  treatment?: number;
  active_exp: number;
  inactive_exp: number;
  journeys_count: number;
  journeys_names: string[];
  criteria_graph?: object;
  legacy: boolean;
  locked?: boolean;
  user: UserT;
  tags: TagsT;
  access_grants: AccessGrantAudience[];
  destinations: DestinationsT;
  breakdowns?: string; // But turns into SerializedBreakdown[]
  minimum_audience_export_size: number;
  minimum_audience_report_size: number;
  config?: string;
  custom_attributes?: CustomAttributes;
  loop_status: number;
}

export interface AudienceIndex<
  UserT = unknown,
  TagsT = unknown,
  DestinationsT = unknown,
> {
  created_at: string;
  updated_at: string;
  id: number;
  name: string;
  audience_type: AudienceType.DEFAULT;
  dataset_group_id: number;
  status: string;
  size: number;
  control?: number;
  treatment?: number;
  legacy: boolean;
  journeys_count: number;
  journeys_names: string[];
  user: UserT;
  tags: TagsT;
  destinations: DestinationsT;
  locked?: boolean;
}

export interface AudienceTemplate<
  UserT = unknown,
  TagsT = unknown,
  DestinationsT = unknown,
> extends Omit<Audience<UserT, TagsT, DestinationsT>, 'audience_type'> {
  audience_type: AudienceType.TEMPLATE;
  legacy: false;
  locked?: boolean;
}

export interface AudienceParticle {
  id: number;
  name: string;
  size: number;
  dataset_group_id: number;
}

export interface Tag {
  created_at: string;
  updated_at: string;
  id: number;
  name: string;
  taggings_count: number;
}
export interface TagSlim {
  id: number;
  name: string;
}

export interface AudienceWithTags extends Audience {
  tags: Tag[];
}

export type CustomAttributes = Record<string, string>;

export type Hash = Record<string, string>;

export interface AudienceWithDestinations extends Audience {
  destinations: DestinationConnection[];
}

export type AudienceRefreshSizeResponse = {
  data?: {
    updated_at: string;
    count: number;
  };
};

export type AudienceExportFormData = {
  platform_type: Nullable<PlatformType>;
  exportName: string;
  mappedMatchFields: MappedMatchField[];
  personalizationFields: Option[];
  privacySettings: MLBPrivacySettings;
  fieldMappings: ZetaFieldMappings;
};

//TODO (thiago) align with audience scheduling
export enum AudienceExportTypes {
  ONGOING = 'ongoing',
  ONE_TIME = 'single_export',
}

export type BaseAudienceExportConfig = {
  campaignType: AudienceExportTypes;
  audienceName: string;
  syncFrequency: string;
  matchFields: MappedMatchField[];
  personalizationFields: Option[];
  endsAt: string;
};

export type ConfigFormData = BaseAudienceExportConfig & {
  privacySettings: MLBPrivacySettings;
  fieldMappings: ZetaFieldMappings;
};

export enum AudienceSyncFrequencies {
  FIFTEEN_MINUTES = 'fifteen_minutes',
  THIRTY_MINUTES = 'thirty_minutes',
  HOURLY = 'hourly',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

export const audienceSyncFrequencyLabels: Record<
  AudienceSyncFrequencies,
  string
> = {
  [AudienceSyncFrequencies.FIFTEEN_MINUTES]: '15 Minutes',
  [AudienceSyncFrequencies.THIRTY_MINUTES]: '30 Minutes',
  [AudienceSyncFrequencies.HOURLY]: 'Hourly',
  [AudienceSyncFrequencies.DAILY]: 'Daily',
  [AudienceSyncFrequencies.WEEKLY]: 'Weekly',
  [AudienceSyncFrequencies.MONTHLY]: 'Monthly',
};

export enum AudienceShowTabs {
  AUDIENCES = 'Overview',
  PERFORMANCE = 'Performance',
  EXPORTS = 'Exports',
  QUERIES = 'Queries',
  INSIGHTS = 'Insights',
  SETTINGS = 'Settings',
}

export interface DestinationFilter {
  label: string;
  value: string;
  icon: string;
}

type AudiencePerformanceInsight = {
  insight: string;
  icon: string;
};

export const AudiencePerformanceInsights: AudiencePerformanceInsight[] = [
  {insight: 'ads', icon: 'web_asset'},
  {insight: 'email', icon: 'mail'},
];
